import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Check, Search, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const AuditPage = () => {
  const [form, setForm] = useState({ name: '', email: '', agency: '', website_url: '', notes: '' });
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error
  const [errorMsg, setErrorMsg] = useState('');

  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');
    setErrorMsg('');
    try {
      const res = await fetch(`${API}/api/audit/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Something went wrong. Please try again.');
      }
      setStatus('success');
    } catch (err) {
      setStatus('error');
      setErrorMsg(err.message);
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--rank-bg)' }} data-testid="audit-page">
      <Helmet><title>Free AI Visibility Audit — MarqiRank</title></Helmet>

      <section className="py-20 md:py-28">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-16 items-start max-w-6xl mx-auto">
            {/* Left — pitch */}
            <div>
              <span className="inline-block px-4 py-2 rounded-full text-sm font-bold uppercase tracking-wider mb-8" style={{ background: 'rgba(74,53,128,0.18)', color: 'var(--rank-accent)' }}>
                Free · No Commitment
              </span>
              <h1 className="display-md text-[var(--text-primary)] mb-6" data-testid="audit-hero-title">
                Get a Free <span className="text-[var(--rank-accent)]">AI Visibility Audit.</span>
              </h1>
              <p className="body-lg mb-10">
                Tell us the client website you want to check. We'll run it against ChatGPT, Gemini, and
                Perplexity — and send you a baseline report showing where they appear, where they don't, and
                who's beating them. Fully white-label, ready to share with your client.
              </p>
              <ul className="space-y-4">
                {['AI Share of Voice across the major engines', 'Competitor citation comparison', 'Quick-win recommendations you can action', 'Delivered under your agency brand'].map((item) => (
                  <li key={item} className="flex items-start space-x-3">
                    <Check className="w-5 h-5 text-[var(--rank-accent)] flex-shrink-0 mt-0.5" />
                    <span className="text-[var(--text-secondary)]">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Right — form / success */}
            <div className="feature-card">
              {status === 'success' ? (
                <div className="text-center py-8" data-testid="audit-success">
                  <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ background: 'rgba(74,53,128,0.18)' }}>
                    <Check className="w-8 h-8 text-[var(--rank-accent)]" />
                  </div>
                  <h2 className="h2 text-[var(--text-primary)] mb-4">Request Received.</h2>
                  <p className="body-md mb-8">
                    Your free AI visibility audit is queued. We'll review {form.website_url ? <span className="text-[var(--rank-accent)]">{form.website_url}</span> : 'your client site'} and get back to you within 24 hours.
                  </p>
                  <Link to="/rank" className="btn-secondary" data-testid="audit-back-link">Back to MarqiRank</Link>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-testid="audit-form">
                  <h2 className="h3 text-[var(--text-primary)] mb-6">Request your audit</h2>

                  <div className="space-y-5">
                    <div>
                      <label className="block text-sm text-[var(--text-secondary)] mb-2">Your name *</label>
                      <input type="text" required value={form.name} onChange={update('name')}
                        className="w-full px-4 py-3 rounded-lg bg-[var(--bg-tertiary)] border border-[var(--border-subtle)] text-[var(--text-primary)] focus:border-[var(--rank-accent)] focus:outline-none transition-colors"
                        placeholder="Jane Smith" data-testid="audit-input-name" />
                    </div>
                    <div>
                      <label className="block text-sm text-[var(--text-secondary)] mb-2">Work email *</label>
                      <input type="email" required value={form.email} onChange={update('email')}
                        className="w-full px-4 py-3 rounded-lg bg-[var(--bg-tertiary)] border border-[var(--border-subtle)] text-[var(--text-primary)] focus:border-[var(--rank-accent)] focus:outline-none transition-colors"
                        placeholder="jane@youragency.com" data-testid="audit-input-email" />
                    </div>
                    <div>
                      <label className="block text-sm text-[var(--text-secondary)] mb-2">Agency name</label>
                      <input type="text" value={form.agency} onChange={update('agency')}
                        className="w-full px-4 py-3 rounded-lg bg-[var(--bg-tertiary)] border border-[var(--border-subtle)] text-[var(--text-primary)] focus:border-[var(--rank-accent)] focus:outline-none transition-colors"
                        placeholder="Your Agency Ltd" data-testid="audit-input-agency" />
                    </div>
                    <div>
                      <label className="block text-sm text-[var(--text-secondary)] mb-2">Client website to audit *</label>
                      <div className="relative">
                        <Search className="w-5 h-5 text-[var(--text-muted)] absolute left-4 top-1/2 -translate-y-1/2" />
                        <input type="text" required value={form.website_url} onChange={update('website_url')}
                          className="w-full pl-11 pr-4 py-3 rounded-lg bg-[var(--bg-tertiary)] border border-[var(--border-subtle)] text-[var(--text-primary)] focus:border-[var(--rank-accent)] focus:outline-none transition-colors"
                          placeholder="clientwebsite.com" data-testid="audit-input-website" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm text-[var(--text-secondary)] mb-2">Anything else? (optional)</label>
                      <textarea rows="3" value={form.notes} onChange={update('notes')}
                        className="w-full px-4 py-3 rounded-lg bg-[var(--bg-tertiary)] border border-[var(--border-subtle)] text-[var(--text-primary)] focus:border-[var(--rank-accent)] focus:outline-none transition-colors resize-none"
                        placeholder="Client industry, key competitors, target locations…" data-testid="audit-input-notes" />
                    </div>
                  </div>

                  {status === 'error' && (
                    <p className="text-red-400 text-sm mt-4" data-testid="audit-error">{errorMsg}</p>
                  )}

                  <button type="submit" disabled={status === 'submitting'}
                    className="btn-primary w-full mt-8 disabled:opacity-60"
                    data-testid="audit-submit-button">
                    {status === 'submitting' ? (
                      <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Submitting…</>
                    ) : (
                      <>Get My Free Audit <ArrowRight className="ml-2 w-5 h-5" /></>
                    )}
                  </button>
                  <p className="text-xs text-[var(--text-muted)] text-center mt-4">
                    No spam. We only use this to prepare and send your audit.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AuditPage;
